<template>
  <div class="flex flex-col overflow-hidden bg-white">
    <Header :brandInfo="brandInfo" class="min-h-[125px]"/>

    <main class="grow">
      <!-- ============ Hero ============ -->
      <section class="relative overflow-hidden bg-slate-950 text-white">
        <div class="absolute inset-0 pointer-events-none" aria-hidden="true">
          <div class="absolute -top-40 -right-32 h-[520px] w-[520px] rounded-full bg-emerald-500/20 blur-3xl"/>
          <div class="absolute -bottom-48 -left-24 h-[480px] w-[480px] rounded-full bg-sky-500/10 blur-3xl"/>
          <div class="absolute inset-0 opacity-[0.07] hero-grid"/>
        </div>

        <div class="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-16 lg:pt-20 lg:pb-24 grid lg:grid-cols-12 gap-12 items-center">
          <div class="lg:col-span-7 reveal reveal-1">
            <p class="inline-flex items-center gap-2 text-[11px] font-semibold tracking-[0.22em] uppercase text-emerald-300">
              <span class="h-px w-6 bg-emerald-400"/>
              Florida · Division of Corporations
            </p>
            <h1 class="mt-5 text-4xl sm:text-5xl lg:text-[56px] font-bold leading-[1.05] tracking-tight">
              Your Florida company was dissolved.<br class="hidden sm:block">
              <span class="text-emerald-400">Let's bring it back.</span>
            </h1>
            <p class="mt-6 max-w-xl text-lg text-slate-300 leading-relaxed">
              Miss one annual report and the state administratively dissolves your LLC or corporation.
              Reinstatement restores it — same name, same document number, same history. We prepare and file the
              application, pay the state fee on your behalf, and send confirmation when you're back in good standing.
            </p>

            <!-- Inline document-number start -->
            <form @submit.prevent="startReinstatement" class="mt-8 max-w-xl">
              <label for="hero-document-number" class="sr-only">Florida document number</label>
              <div class="flex flex-col sm:flex-row gap-3">
                <input
                  id="hero-document-number"
                  v-model="documentNumberInput"
                  type="text"
                  autocomplete="off"
                  placeholder="Document number, e.g. L21000123456"
                  class="flex-1 rounded-xl border-0 bg-white/95 px-5 py-4 text-base text-slate-900 placeholder-slate-400 shadow-lg focus:ring-2 focus:ring-emerald-400"
                  :class="{ 'ring-2 ring-red-400': searchError }"
                />
                <button type="submit"
                        class="inline-flex items-center justify-center gap-2 rounded-xl bg-emerald-500 px-7 py-4 text-base font-bold text-slate-950 shadow-lg shadow-emerald-500/30 transition hover:bg-emerald-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-white">
                  Reinstate my company
                  <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                    <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
                  </svg>
                </button>
              </div>
              <p v-if="searchError" class="mt-2 text-sm text-red-300">{{ searchError }}</p>
              <p v-else class="mt-3 text-sm text-slate-400">
                Don't know it?
                <a :href="searchByNameUrl" class="text-emerald-300 underline-offset-2 hover:underline">Look up your company by name</a>.
              </p>
            </form>

            <ul class="mt-8 flex flex-wrap gap-x-6 gap-y-3 text-sm text-slate-300">
              <li v-for="chip in heroChips" :key="chip" class="flex items-center gap-2">
                <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 text-emerald-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>
                {{ chip }}
              </li>
            </ul>
          </div>

          <!-- Status card -->
          <div class="lg:col-span-5 reveal reveal-2">
            <div class="relative mx-auto max-w-md rounded-2xl border border-white/10 bg-white/[0.06] p-6 sm:p-7 backdrop-blur-sm shadow-2xl">
              <div class="flex items-center justify-between gap-3">
                <span class="text-[11px] font-semibold tracking-[0.18em] uppercase text-slate-400">Sunbiz status</span>
                <span class="rounded-full border border-red-400/40 bg-red-500/15 px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wider text-red-200">Dissolved</span>
              </div>
              <div class="mt-6 flex items-center gap-4">
                <div class="flex-1 rounded-xl border border-red-400/30 bg-red-500/10 p-4 text-center">
                  <p class="text-[10px] font-semibold uppercase tracking-wider text-red-200/80">Today</p>
                  <p class="mt-1 text-lg font-bold text-red-100">Inactive</p>
                </div>
                <svg xmlns="http://www.w3.org/2000/svg" class="w-7 h-7 text-emerald-400 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
                <div class="flex-1 rounded-xl border border-emerald-400/30 bg-emerald-500/10 p-4 text-center">
                  <p class="text-[10px] font-semibold uppercase tracking-wider text-emerald-200/80">After filing</p>
                  <p class="mt-1 text-lg font-bold text-emerald-100">Active</p>
                </div>
              </div>

              <dl class="mt-6 space-y-2.5 text-sm">
                <div class="flex items-baseline justify-between gap-4 border-b border-white/10 pb-2.5">
                  <dt class="text-slate-300">Florida state fee <span class="text-slate-500">(LLC, one report year)</span></dt>
                  <dd class="font-semibold tabular-nums">${{ pricing.state_fee }}</dd>
                </div>
                <div class="flex items-baseline justify-between gap-4 border-b border-white/10 pb-2.5">
                  <dt class="text-slate-300">Preparation &amp; filing service</dt>
                  <dd class="font-semibold tabular-nums">${{ pricing.service }}</dd>
                </div>
                <div class="flex items-baseline justify-between gap-4 pt-1">
                  <dt class="font-semibold">Total from</dt>
                  <dd class="text-2xl font-bold tabular-nums text-emerald-300">${{ totalDueToday }}</dd>
                </div>
              </dl>
              <p class="mt-3 text-[11px] text-slate-400 leading-snug">The state fee depends on entity type and how many annual reports are delinquent. We quote the exact amount from your Sunbiz record before you pay.</p>
              <p class="mt-4 rounded-lg bg-emerald-500/10 border border-emerald-400/20 px-3 py-2 text-[12px] text-emerald-100 leading-snug">
                <span class="font-bold">Included, $0 today:</span> automatic Annual Report filing from {{ pricing.first_renewal_date }} so this never happens again. Optional — untick it at checkout.
              </p>
              <p class="mt-4 text-[10px] uppercase tracking-[0.15em] text-slate-500">Filed by {{ brandName }}</p>
            </div>
          </div>
        </div>
      </section>

      <!-- ============ What happened ============ -->
      <section class="py-16 lg:py-24 bg-white">
        <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-10">
          <div class="lg:col-span-5">
            <p class="text-[11px] font-semibold tracking-[0.22em] uppercase text-emerald-700">What happened</p>
            <h2 class="mt-3 text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">Administrative dissolution, explained.</h2>
            <p class="mt-5 text-lg text-slate-600 leading-relaxed">
              Every Florida LLC and corporation must file an annual report between January 1 and May 1. Miss it, and
              the state adds a $400 late fee. Still unfiled by the fourth Friday of September, and the Division of
              Corporations administratively dissolves the company.
            </p>
            <p class="mt-4 text-lg text-slate-600 leading-relaxed">
              Dissolution isn't the end — Florida lets you reinstate at any time by filing a reinstatement application
              and paying the delinquent fees. That's exactly what we do for you.
            </p>
          </div>
          <div class="lg:col-span-7 grid sm:grid-cols-2 gap-5">
            <div v-for="risk in risks" :key="risk.title" class="rounded-2xl border border-slate-200 bg-slate-50/70 p-6">
              <span class="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-white border border-slate-200 text-slate-700 shadow-sm">
                <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" v-html="risk.icon"/>
              </span>
              <h3 class="mt-4 text-base font-bold text-slate-900">{{ risk.title }}</h3>
              <p class="mt-1.5 text-sm text-slate-600 leading-relaxed">{{ risk.body }}</p>
            </div>
          </div>
        </div>
      </section>

      <!-- ============ What's included ============ -->
      <section class="py-16 lg:py-24 bg-slate-50 border-y border-slate-200">
        <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div class="max-w-2xl">
            <p class="text-[11px] font-semibold tracking-[0.22em] uppercase text-emerald-700">What's included</p>
            <h2 class="mt-3 text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">One fee. Everything the state requires.</h2>
          </div>
          <div class="mt-12 grid md:grid-cols-2 gap-6">
            <div v-for="item in includes" :key="item.title" class="flex items-start gap-4 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <span class="mt-0.5 inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
                <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>
              </span>
              <div>
                <h3 class="text-lg font-bold text-slate-900">{{ item.title }}</h3>
                <p class="mt-1.5 text-[15px] text-slate-600 leading-relaxed">{{ item.body }}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- ============ Pricing ============ -->
      <section class="py-16 lg:py-24 bg-white">
        <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-10 items-start">
          <div class="lg:col-span-5">
            <p class="text-[11px] font-semibold tracking-[0.22em] uppercase text-emerald-700">Pricing</p>
            <h2 class="mt-3 text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">Transparent, all-in pricing.</h2>
            <p class="mt-5 text-lg text-slate-600 leading-relaxed">
              The state fee is passed through at cost. Our service fee covers preparing the application, filing the
              delinquent annual report, handling any follow-up with the Division of Corporations, and your
              confirmation of good standing.
            </p>
            <button type="button" @click="scrollToSearch"
                    class="mt-8 inline-flex items-center gap-2 rounded-xl bg-slate-900 px-6 py-3.5 text-base font-semibold text-white shadow-sm hover:bg-slate-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900">
              Start my reinstatement
              <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
            </button>
          </div>
          <div class="lg:col-span-7">
            <div class="rounded-3xl border border-slate-200 bg-slate-950 text-white p-7 sm:p-9 shadow-xl">
              <div class="flex items-baseline justify-between gap-4">
                <div>
                  <p class="text-[11px] font-semibold tracking-[0.18em] uppercase text-slate-400">Florida company reinstatement</p>
                  <p class="mt-1 text-sm text-slate-300">Our fee plus the state's fee for your entity type</p>
                </div>
                <p class="text-4xl sm:text-5xl font-bold tabular-nums">from ${{ totalDueToday }}</p>
              </div>

              <!-- Florida fee schedule -->
              <div class="mt-7 overflow-hidden rounded-xl border border-white/10">
                <table class="w-full text-sm">
                  <thead class="bg-white/5 text-[11px] uppercase tracking-wider text-slate-400">
                    <tr>
                      <th class="px-3 py-2 text-left font-semibold">Entity</th>
                      <th class="px-3 py-2 text-right font-semibold">Reinstatement fee</th>
                      <th class="px-3 py-2 text-right font-semibold">Per report year</th>
                      <th class="px-3 py-2 text-right font-semibold">1st year total*</th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-white/10">
                    <tr v-for="row in pricing.schedule" :key="row.type">
                      <td class="px-3 py-2 text-slate-200">{{ row.label }}</td>
                      <td class="px-3 py-2 text-right tabular-nums">${{ row.reinstatement_fee }}</td>
                      <td class="px-3 py-2 text-right tabular-nums">${{ row.per_report_year }}</td>
                      <td class="px-3 py-2 text-right tabular-nums font-semibold text-emerald-300">${{ row.first_year_total }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p class="mt-2 text-[11px] text-slate-500">*State fee for one delinquent report year plus our ${{ pricing.service }} service fee. Each additional missed report year adds the per-year fee; limited partnerships also pay the reinstatement fee for each year revoked.</p>

              <ul class="mt-6 space-y-3 text-sm">
                <li class="flex items-baseline justify-between gap-4 border-b border-white/10 pb-3">
                  <span class="text-slate-300">Reinstatement preparation &amp; filing service</span>
                  <span class="font-semibold tabular-nums">${{ pricing.service }}</span>
                </li>
                <li class="flex items-baseline justify-between gap-4 pb-1">
                  <span class="text-slate-300">
                    Automatic Annual Report filing from {{ pricing.first_renewal_date }}
                    <span class="ml-1 inline-block rounded bg-emerald-500/20 px-1.5 py-0.5 text-[10px] font-bold text-emerald-200 align-middle">$0 TODAY</span>
                  </span>
                  <span class="font-semibold tabular-nums text-emerald-300">$0.00</span>
                </li>
              </ul>
              <p class="mt-5 text-[12px] text-slate-400 leading-relaxed">
                The automatic annual report enrollment renews at ${{ pricing.annual_report_renewal }}/yr, first charged
                {{ pricing.first_renewal_date }} when we file next year's report ahead of the deadline. It is optional —
                untick it at checkout, or cancel any time before that date at no charge.
              </p>
            </div>
          </div>
        </div>
      </section>

      <!-- ============ How it works ============ -->
      <section class="py-16 lg:py-24 bg-slate-50 border-y border-slate-200">
        <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div class="max-w-2xl">
            <p class="text-[11px] font-semibold tracking-[0.22em] uppercase text-emerald-700">Process</p>
            <h2 class="mt-3 text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">Back in good standing in three steps.</h2>
          </div>
          <ol class="mt-12 grid gap-8 lg:grid-cols-3">
            <li v-for="step in steps" :key="step.num" class="relative rounded-2xl border border-slate-200 bg-white p-7 shadow-sm">
              <span class="text-sm font-bold tabular-nums text-emerald-700">{{ step.num }}</span>
              <h3 class="mt-3 text-lg font-bold text-slate-900">{{ step.title }}</h3>
              <p class="mt-2 text-[15px] text-slate-600 leading-relaxed">{{ step.body }}</p>
            </li>
          </ol>
        </div>
      </section>

      <!-- ============ FAQ ============ -->
      <section class="py-16 lg:py-24 bg-white">
        <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-10">
          <div class="lg:col-span-4">
            <p class="text-[11px] font-semibold tracking-[0.22em] uppercase text-emerald-700">Questions</p>
            <h2 class="mt-3 text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">Before you reinstate.</h2>
          </div>
          <div class="lg:col-span-8 flex flex-col gap-3">
            <details v-for="faq in faqs" :key="faq.q" class="group rounded-2xl border border-slate-200 bg-white p-6 open:shadow-sm">
              <summary class="flex cursor-pointer list-none items-center justify-between gap-4 text-lg font-semibold text-slate-900 [&::-webkit-details-marker]:hidden">
                {{ faq.q }}
                <span class="text-xl text-emerald-700 transition-transform duration-200 group-open:rotate-45" aria-hidden="true">+</span>
              </summary>
              <p class="mt-4 text-[15px] text-slate-600 leading-relaxed">{{ faq.a }}</p>
            </details>
          </div>
        </div>
      </section>

      <!-- ============ CTA band / search ============ -->
      <section id="reinstatement-search" class="relative overflow-hidden bg-slate-950 py-16 lg:py-24 text-white">
        <div class="absolute -bottom-32 -right-20 h-[380px] w-[380px] rounded-full border border-white/10" aria-hidden="true">
          <div class="absolute inset-6 rounded-full border border-white/10"/>
          <div class="absolute inset-14 rounded-full border border-white/[0.07]"/>
        </div>
        <div class="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 class="text-3xl sm:text-4xl font-bold tracking-tight">Ready to reactivate your company?</h2>
          <p class="mt-4 text-lg text-slate-300">Enter your Florida document number. We pull what the state still has on file so the form takes minutes, not an afternoon.</p>
          <form @submit.prevent="startReinstatement" class="mt-8 max-w-xl mx-auto">
            <label for="cta-document-number" class="sr-only">Florida document number</label>
            <div class="flex flex-col sm:flex-row gap-3">
              <input
                id="cta-document-number"
                v-model="documentNumberInput"
                type="text"
                autocomplete="off"
                placeholder="e.g. L21000123456 or P19000045678"
                class="flex-1 rounded-xl border-0 bg-white px-5 py-4 text-base text-slate-900 placeholder-slate-400 shadow-lg focus:ring-2 focus:ring-emerald-400"
                :class="{ 'ring-2 ring-red-400': searchError }"
              />
              <button type="submit"
                      class="inline-flex items-center justify-center rounded-xl bg-emerald-500 px-7 py-4 text-base font-bold text-slate-950 shadow-lg shadow-emerald-500/30 transition hover:bg-emerald-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-white">
                Start reinstatement
              </button>
            </div>
            <p v-if="searchError" class="mt-2 text-sm text-red-300 text-left">{{ searchError }}</p>
          </form>
          <p class="mt-6 text-sm text-slate-400">
            Your document number starts with a letter (L for LLC, P for corporation, N for non-profit) followed by numbers.
            <a :href="searchByNameUrl" class="text-emerald-300 underline-offset-2 hover:underline">Search by company name</a> if you don't have it.
          </p>
        </div>
      </section>
    </main>

    <Footer :brandInfo="brandInfo"/>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import Header from '../../partials/HeaderMini.vue'
import Footer from '../../partials/Footer.vue'
import { usePageViewTracking } from '../../composables/usePageViewTracking'

const props = defineProps({
  brandInfo: { type: Object, required: false, default: null },
  documentNumber: { type: String, required: false, default: null },
  pricing: {
    type: Object,
    required: false,
    default: () => ({
      service: '350.00',
      state_fee: '238.75',
      schedule: [],
      from_total: '588.75',
      annual_report_renewal: '0.00',
      first_renewal_date: `January 3, ${new Date().getFullYear() + 1}`,
    }),
  },
})

const { trackPageView, withCampaignParams } = usePageViewTracking()

const documentNumberInput = ref(props.documentNumber ?? '')
const searchError = ref('')
const searchByNameUrl = '/'

const brandName = computed(() => props.brandInfo?.name ?? 'Business Compliance Services')

// Lowest possible total: an LLC owing one report year plus the service fee.
const totalDueToday = computed(() => {
  if (props.pricing?.from_total) return props.pricing.from_total
  const total = Number(props.pricing?.service ?? 0) + Number(props.pricing?.state_fee ?? 0)
  return total.toFixed(2)
})

const heroChips = [
  'State fee included',
  'Filed within 1 business day',
  'Confirmation of good standing',
  'No hidden fees',
]

const risks = [
  {
    title: 'Your name is up for grabs',
    body: 'Name protection ends the day the company is dissolved. Another business can register your exact name until you reinstate.',
    icon: '<path d="M12 2l7 4v6c0 5-3.5 9-7 10-3.5-1-7-5-7-10V6l7-4z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="16" x2="12.01" y2="16"/>',
  },
  {
    title: 'Personal exposure',
    body: 'Contracts signed and income earned while dissolved may not be shielded by the company, leaving owners personally on the hook.',
    icon: '<circle cx="12" cy="8" r="4"/><path d="M4 21v-1a6 6 0 0 1 6-6h4a6 6 0 0 1 6 6v1"/>',
  },
  {
    title: 'Banks and vendors check Sunbiz',
    body: 'Lenders, payment processors and licensing boards look up your status. "Inactive" stalls accounts, financing and renewals.',
    icon: '<rect x="2" y="5" width="20" height="14" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/>',
  },
  {
    title: 'Fees keep stacking',
    body: 'Each year a dissolved company stays unfiled adds another delinquent annual report fee to the reinstatement bill.',
    icon: '<line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>',
  },
]

const includes = [
  { title: 'Reinstatement application prepared and filed', body: 'We complete the Florida reinstatement application with your current addresses, registered agent and officers or managers, and submit it to the Division of Corporations.' },
  { title: 'Delinquent annual report included', body: 'Reinstatement requires the missed annual report. It is prepared and filed together with the application, with the state fee paid on your behalf.' },
  { title: 'Registered agent and record review', body: 'We confirm the agent of record accepts the appointment and update anything the state still has wrong before filing.' },
  { title: 'Confirmation of good standing', body: 'When Sunbiz shows the company active again, confirmation lands in your inbox, ready for your bank, lenders and licensing agencies.' },
]

const steps = [
  { num: '01', title: 'Confirm your company details', body: 'Enter your document number. We prefill what Florida still has on record; you confirm addresses, agent and management.' },
  { num: '02', title: 'We prepare and file', body: 'A person reviews the application and the delinquent annual report, then files both with the Division of Corporations within 1 business day.' },
  { num: '03', title: 'Your company is active again', body: 'The state restores your company to active status. You get confirmation, and automatic annual report filing keeps it that way.' },
]

const faqs = [
  { q: 'Can any dissolved Florida company be reinstated?', a: 'Companies that were administratively dissolved or revoked for not filing an annual report can be reinstated at any time, as long as the name is still available. Companies that filed voluntary articles of dissolution follow a different process — contact us and we will point you in the right direction.' },
  { q: 'Does the company keep its name and document number?', a: 'Yes. Reinstatement continues the original company: same document number, same formation date, same EIN. If another business registered your name while you were dissolved, the state will require a new name on the application.' },
  { q: 'What does the state fee cover?', a: 'The Florida reinstatement fee for your entity type plus the annual report fee for every year that was missed. We pull the exact amount for your document number from Sunbiz and show it before you pay.' },
  { q: 'How long does reinstatement take?', a: 'We file within one business day of receiving your completed application. The Division of Corporations typically processes online reinstatements in a few business days, and your Sunbiz record updates as soon as it is accepted.' },
  { q: 'What is the automatic annual report enrollment?', a: 'Reinstated companies owe an annual report again next year. The enrollment means we file it for you automatically in January ahead of the May 1 deadline. It costs nothing today, renews at the normal annual report price, and you can untick it at checkout or cancel any time before the first charge.' },
]

const scrollToSearch = () => {
  document.querySelector('#reinstatement-search')?.scrollIntoView({ behavior: 'smooth' })
}

const startReinstatement = () => {
  searchError.value = ''
  const trimmed = documentNumberInput.value.trim().toUpperCase()

  if (!trimmed) {
    searchError.value = 'Please enter your Florida document number.'
    return
  }
  if (!/^[A-Z]\d{5,}$/.test(trimmed)) {
    searchError.value = 'Document numbers start with a letter followed by digits, e.g. L21000123456.'
    return
  }

  window.location.href = withCampaignParams(`/reinstate/${trimmed}`)
}

onMounted(() => {
  trackPageView('landing', props.documentNumber)
})
</script>

<style scoped>
.hero-grid {
  background-image:
    linear-gradient(to right, rgba(255, 255, 255, 0.5) 1px, transparent 1px),
    linear-gradient(to bottom, rgba(255, 255, 255, 0.5) 1px, transparent 1px);
  background-size: 48px 48px;
  mask-image: radial-gradient(ellipse at center, black 30%, transparent 75%);
}
.reveal {
  opacity: 0;
  transform: translateY(8px);
  animation: reveal-in 560ms cubic-bezier(0.22, 1, 0.36, 1) forwards;
}
.reveal-1 { animation-delay: 60ms; }
.reveal-2 { animation-delay: 180ms; }
@keyframes reveal-in { to { opacity: 1; transform: translateY(0); } }
@media (prefers-reduced-motion: reduce) {
  .reveal { animation: none; opacity: 1; transform: none; }
}
</style>
